import "./styles.css";
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import Header from "./components/Header";
import ProjectsGrid from "./components/ProjectsGrid";
import About from "./components/About";
import Footer from "./components/Footer";
import FontLoader from "./components/FontLoader";
import PasswordGate from "./components/PasswordGate";
import { verifyPassword, logAccess } from "./lib/supabase";
import Cookies from "js-cookie";
import { PROJECT_LAYOUT } from "./data/layout";

// Future flag configurations to suppress warnings
const ROUTER_FUTURE_FLAGS = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

// Cookie name constant
const AUTH_COOKIE_NAME = "portfolio_auth";
// Cookie expiry in days
const COOKIE_EXPIRY_DAYS = 7;

// PRODUCTION MODE - set to false
const BYPASS_AUTH = false;

// New ScrollToTop component to handle scroll reset
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <TransitionGroup>
      <CSSTransition 
        key={location.pathname} 
        classNames="page" 
        timeout={800}
        // Optional: add unmountOnExit to clean up exited routes
        unmountOnExit
      >
        <Routes location={location}>
          <Route 
            path="/" 
            element={<Navigate to="/work" replace />} 
          />
          <Route path="/work" element={<ProjectsGrid />} />
          <Route path="/about" element={<About />} />
          {/* Catch-all route to redirect to home */}
          <Route path="*" element={<Navigate to="/work" replace />} />
        </Routes>
      </CSSTransition>
    </TransitionGroup>
  );
}

function AppContent() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(BYPASS_AUTH);
  const [organization, setOrganization] = useState("dev");
  const [isCheckingAuth, setIsCheckingAuth] = useState(!BYPASS_AUTH);

  useEffect(() => {
    // Slight delay to trigger initial page load animation
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  // Check for existing authentication cookie on mount
  useEffect(() => {
    if (BYPASS_AUTH) return; // Skip auth check in dev mode

    const checkAuth = async () => {
      const authCookie = Cookies.get(AUTH_COOKIE_NAME);
      if (authCookie) {
        try {
          const cookieData = JSON.parse(authCookie);
          const result = await verifyPassword(cookieData.password);

          if (result?.organization) {
            setIsAuthenticated(true);
            setOrganization(result.organization);
          } else {
            // If verification fails, remove the invalid cookie
            Cookies.remove(AUTH_COOKIE_NAME);
          }
        } catch (error) {
          // If there's an error parsing the cookie, remove it
          Cookies.remove(AUTH_COOKIE_NAME);
        }
      }
      setIsCheckingAuth(false);
    };

    checkAuth();
  }, []);

  const handleAuthentication = async (password) => {
    const result = await verifyPassword(password);

    if (result?.organization) {
      // Attempt to get IP address using external service
      let ipAddress = 'Unknown';
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        ipAddress = data.ip;
      } catch (error) {
        // Silently handle IP retrieval failure
      }

      // Store authentication in a cookie
      Cookies.set(
        AUTH_COOKIE_NAME,
        JSON.stringify({ password, organization: result.organization }),
        { expires: COOKIE_EXPIRY_DAYS }
      );

      setIsAuthenticated(true);
      setOrganization(result.organization);
      await logAccess(result.organization, ipAddress, navigator.userAgent);
      return true;
    }
    return false;
  };

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return null;
  }

  if (!isAuthenticated) {
    return <PasswordGate onAuthenticate={handleAuthentication} />;
  }

  return (
    <Router future={ROUTER_FUTURE_FLAGS}>
      <ScrollToTop />
      <div className={`app-wrapper layout-${PROJECT_LAYOUT} ${isLoaded ? "page-loaded" : ""}`}>
        <div className="container">
          <Header />
          <AnimatedRoutes />
          <Footer />
        </div>
      </div>
    </Router>
  );
}

export default function App() {
  return (
    <FontLoader>
      <AppContent />
    </FontLoader>
  );
}